/**
 * Sessions - 访客会话常量
 * 渠道 Tab/标签改由 stores/channels 从接口获取
 */

/** 访客会话视图模式（账号视图/汇总视图）本地存储 key */
export const VISITOR_SESSIONS_VIEW_MODE_KEY = 'visitor-sessions-view-mode';

/** 实时会话顶部筛选区域高度本地存储 key */
export const VISITOR_SESSIONS_FILTER_PANEL_HEIGHT_KEY = 'visitor-sessions-filter-panel-height';

/** 实时会话「更多筛选」展开状态本地存储 key，需与筛选区高度一同恢复，否则刷新后高度与内容不匹配 */
export const VISITOR_SESSIONS_FILTER_EXPANDED_KEY = 'visitor-sessions-filter-expanded';

/** 会话列表、用户详情两列的拖拽宽度本地存储 key */
export const VISITOR_SESSIONS_LIST_WIDTH_KEY = 'visitor-sessions-list-width';
export const VISITOR_SESSIONS_DETAIL_WIDTH_KEY = 'visitor-sessions-detail-width';

/**
 * 两列拖拽的宽度区间。
 *
 * min 是正常拖拽的最窄宽度；继续往里拖到窄于 collapseAt 时整列收起、不占宽度，
 * 边缘留一条把手，点一下或往外拖恢复。上限保证中间对话区不被压得过窄。
 */
export const VISITOR_SESSIONS_LIST_WIDTH = {
  default: 300,
  min: 240,
  max: 480,
  collapseAt: 180,
};
export const VISITOR_SESSIONS_DETAIL_WIDTH = {
  default: 270,
  min: 220,
  max: 460,
  collapseAt: 160,
};

/** 渠道 key 映射（用于匹配账号 channel） */
export const CHANNEL_KEYS: Record<string, string[]> = {
  '': [],
  dy: ['dy', 'douyin'],
  dy_feige: ['dy_feige', '抖音飞鸽', '飞鸽'],
  wx: ['wechat_mp', 'wechat_official', 'wx', '微信公众号'],
  tencent: ['tencent', 'fmc', '腾讯咨询', '腾讯资讯'],
  xhs: ['xhs', 'xiaohongshu'],
  kuaishou: ['kuaishou'],
  web: ['web', '网站咨询', '网站资讯'],
  baidu: ['baidu', '百度营销', 'baidu_chat'],
};

/** 开口状态选项（open_status：1=开口, 2=沉默, 3=屏蔽） */
export const OPEN_STATUS_OPTIONS = [
  { value: 1, label: '开口' },
  { value: 2, label: '沉默' },
  { value: 3, label: '屏蔽' },
];

/** 留资状态选项（对应 has_clue） */
export const LEAD_STATUS_OPTIONS = [
  { value: '', label: '留资状态' },
  { value: '1', label: '已留资' },
  { value: '0', label: '未留资' },
];

/**
 * 访客来源选项（user_source）。
 * 接口支持逗号分隔多值；值不在此表的会被后端直接丢弃。
 */
export const USER_SOURCE_OPTIONS = [
  { value: 1, label: '自然来源-私信' },
  { value: 2, label: '自然来源-意向评论' },
  { value: 3, label: '自然来源-直播营销' },
  { value: 4, label: '广告来源' },
];

/** 拉黑状态选项（is_black：1=已拉黑，0=未拉黑） */
export const BLACK_STATUS_OPTIONS = [
  { value: 1, label: '已拉黑' },
  { value: 0, label: '未拉黑' },
];

/** 标签搜索模式选项 */
export const TAG_SEARCH_MODE_OPTIONS = [
  { value: 'no_tag', label: '无标签' },
  { value: 'all', label: '包含所有' },
  { value: 'exactly', label: '仅包含' },
  { value: 'none', label: '不包含任一' },
  { value: 'any', label: '包含任一' },
];

/** 会话列表 last_message 中 msg_type 到展示文案的映射（key 与后端返回保持一致，兼容数字与字符串） */
export const MSG_TYPE_LABELS: Record<string, string> = {
  text: '',
  image: '[图片]',
  voice: '[语音]',
  video: '[视频]',
  article: '[图文]',
  miniprogram: '[小程序]',
  miniProgram: '[小程序]',
  qaMenu: '[问答菜单]',
  qa_menu: '[问答菜单]',
  textMenu: '[文本菜单]',
  text_menu: '[文本菜单]',
  productCard: '[商品卡片]',
  product_card: '[商品卡片]',
  collectPhone: '[收集号码]',
  collect_phone: '[收集号码]',
  leadCard: '[留资卡片]',
  leads_card: '[留资卡片]',
  businessCard: '[名片]',
  note: '[笔记]',
  landingPage: '[落地页]',
  tradeCard: '[交易卡]',
  transactionCard: '[交易卡]',
  link: '[链接]',
  acquisitionLink: '[获客链接]',
  acquisition_link: '[获客链接]',
  appointmentCard: '[高级预约卡]',
  /** 飞鸽新版留资卡，字符串形态为前端预设兜底，实际取值待后端确认 */
  newLeadCard: '[新版留资卡]',
  /** 快手营销工具卡片，后端发送接口亦接受这些 key 作为 msg_type */
  ksLinkCard: '[链接卡]',
  ksWecomCard: '[企微卡]',
  ksStoreCard: '[门店卡]',
  ksInteractQa: '[互动问答]',
  /** 美团卡片，key 形态与数字形态都要兼容 */
  mtShopCard: '[美团门店卡]',
  mtGoodsCard: '[美团商品卡]',
  // 数字 key 兼容：IM 推送/接口返回 msg_type 为数字时（参见 src/constants/chatBox.ts MsgType 枚举）
  '1': '',
  '2': '[图片]',
  '3': '[语音]',
  '4': '[视频]',
  '5': '[图文]',
  '6': '[留资卡片]',
  '7': '[问答菜单]',
  '8': '[文本菜单]',
  '9': '[小程序]',
  '10': '[商品卡片]',
  '11': '[收集号码]',
  '12': '[获客链接]',
  '13': '[名片]',
  '14': '[笔记]',
  '15': '[落地页]',
  '16': '[交易卡]',
  '17': '[卡片]',
  '18': '[链接]',
  '19': '[高级预约卡]',
  '20': '[新版留资卡]',
  '23': '[平台下发意向评论]',
  '24': '[小红书评论分享]',
  '25': '[链接卡]',
  '26': '[企微卡]',
  '27': '[门店卡]',
  '28': '[互动问答]',
  '29': '[美团门店卡]',
  '30': '[美团商品卡]',
};
